// ImportPolicyUI.jsx
import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import AgencyDropdown from '../../../../utils/AgencyDropdown';
import InsurerDropdown from '../../../../utils/Insurer';
import ProductDropdown from '../../../../utils/ProductDropdown';
import CustomerDropdown from '../../../../utils/CustomerDropdown';
import {
    useUploadExcelMutation,
    useGetBatchesQuery,
    useValidateInsurersMutation,
    useValidateAgenciesMutation,
    useValidateProductsMutation,
    useValidateFupMutation,
    useValidateCustomersMutation,
    useGetInvalidInsurersMutation,
    useGetInvalidProductsMutation,
    useGetInvalidAgenciesMutation,
    useGetInvalidCustomersMutation,
    useGetInvalidFupMutation,
    useApplyCorrectionMutation,
    useFinishImportMutation,
} from '../../../../services/redux/api';
import { RiFileExcel2Line } from 'react-icons/ri';
import { FiUpload, FiSave } from 'react-icons/fi';
import { IterationCw, Save, X } from 'lucide-react';
import { useSaveCustomerMutation, useSaveGroupMutation } from '../../../../services/redux/api';
import { toast } from "react-toastify";
import { API_BASE_URL } from '../../../../services/config';
import { useSelector } from 'react-redux';
import Loader from '../../../../utils/Loader';
export default function ImportPolicyUI() {
    // helper: format incoming date-like values to dd-mm-yyyy for display
    const formatDateShort = (val) => {
        if (!val) return '';
        try {
            const d = new Date(val);
            if (isNaN(d.getTime())) return String(val).split('T')[0] || String(val);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
        } catch (e) {
            return String(val).split('T')[0] || String(val);
        }
    };
    const [fileName, setFileName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]); // track upload history in UI
    const [currentBatch, setCurrentBatch] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isFinishing, setIsFinishing] = useState(false);
    const [counts, setCounts] = useState({
        incorrectInsurer: 0,
        incorrectProduct: 0,
        invalidCustomer: 0,
        incorrectFupDate: 0,
        incorrectAgency: 0,
        canBeImported: 0,
        totalInExcel: 0,
    });
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalType, setModalType] = useState(''); // insurer | product | customer | agency | fup
    const [modalColumns, setModalColumns] = useState({});
    const [modalData, setModalData] = useState([]);
    const [saveCustomer] = useSaveCustomerMutation();
    const [saveGroup] = useSaveGroupMutation();
    // import-file RTK mutations
    const [uploadExcel] = useUploadExcelMutation();
    const { data: batchesResp, isLoading: batchesLoading, refetch: refetchBatches } = useGetBatchesQuery();
    const [validateInsurers] = useValidateInsurersMutation();
    const [validateAgencies] = useValidateAgenciesMutation();
    const [validateProducts] = useValidateProductsMutation();
    const [validateFup] = useValidateFupMutation();
    const [validateCustomers] = useValidateCustomersMutation();
    const [getInvalidInsurers] = useGetInvalidInsurersMutation();
    const [getInvalidProducts] = useGetInvalidProductsMutation();
    const [getInvalidAgencies] = useGetInvalidAgenciesMutation();
    const [getInvalidCustomers] = useGetInvalidCustomersMutation();
    const [getInvalidFup] = useGetInvalidFupMutation();
    const [applyCorrection] = useApplyCorrectionMutation();
    const [finishImport] = useFinishImportMutation();
    // read token from redux store when available (fallbacks below use localStorage)
    const reduxToken = useSelector((state) => state.auth?.token);
    // per-row customerAction (map | create | createGroupThenAdd) will be stored on each modalData item

    const onFileChange = (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) {
            setSelectedFile(f);
            setFileName(f.name);
        }
    };
    const onBrowseClick = () => {
        document.getElementById('hidden-file-input').click();
    };
    const onStartImport = async () => {
        if (!selectedFile) return toast.info('Please choose a file first');
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('createdBy', 'sukanya');
        try {
            // upload and get batch id + headers information
            const res = await uploadExcel(formData).unwrap();
            const result = res;
            if (result) {
                setCurrentBatch(result.batchId);
                // detect which columns were present in the uploaded file (backend returns headers array)
                const headers = (result.headers || []).map(h => String(h || '').toLowerCase());
                const hasInsurer = headers.some(h => h.includes('insur'));
                const hasProduct = headers.some(h => h.includes('product'));
                const hasAgency = headers.some(h => h.includes('agency'));
                const hasFup = headers.some(h => h.includes('fup')) || headers.some(h => h.includes('fup date'));
                const hasCustomer = headers.some(h => h.includes('email') || h.includes('mobile') || h.includes('name'));

                // run validations conditionally; if column is missing, set 0 for that category
                const vjson = hasInsurer ? await validateInsurers(result.batchId).unwrap() : { counts: { IncorrectInsurer: 0, TotalRows: result.inserted || 0 } };
                const ajson = hasAgency ? await validateAgencies(result.batchId).unwrap() : { counts: { IncorrectAgency: 0, TotalRows: result.inserted || 0 } };
                const pjson = hasProduct ? await validateProducts(result.batchId).unwrap() : { counts: { IncorrectProduct: 0, TotalRows: result.inserted || 0 } };
                const fjson = hasFup ? await validateFup(result.batchId).unwrap() : { counts: { IncorrectFUP: 0, TotalRows: result.inserted || 0 } };
                const cjson = hasCustomer ? await validateCustomers(result.batchId).unwrap() : { counts: { InvalidCustomer: 0, TotalRows: result.inserted || 0 } };

                // compute totals and canBeImported in one place to avoid negative values
                const totalRows = result.inserted || vjson.counts?.TotalRows || ajson.counts?.TotalRows || pjson.counts?.TotalRows || fjson.counts?.TotalRows || cjson.counts?.TotalRows || 0;
                const incorrectInsurer = (vjson.counts && vjson.counts.IncorrectInsurer) ? vjson.counts.IncorrectInsurer : 0;
                const incorrectAgency = (ajson.counts && ajson.counts.IncorrectAgency) ? ajson.counts.IncorrectAgency : 0;
                const incorrectProduct = (pjson.counts && pjson.counts.IncorrectProduct) ? pjson.counts.IncorrectProduct : 0;
                const incorrectFupDate = (fjson.counts && fjson.counts.IncorrectFUP) ? fjson.counts.IncorrectFUP : 0;
                const invalidCustomer = (cjson.counts && cjson.counts.InvalidCustomer) ? cjson.counts.InvalidCustomer : 0;
                const sumInvalid = incorrectInsurer + incorrectAgency + incorrectProduct + incorrectFupDate + invalidCustomer;
                const canBeImported = Math.max(0, totalRows - sumInvalid);

                setCounts({
                    incorrectInsurer,
                    incorrectProduct,
                    invalidCustomer,
                    incorrectFupDate,
                    incorrectAgency,
                    totalInExcel: totalRows,
                    canBeImported
                });
                // Trigger refetch of server-side batches and keep a transient frontend record
                try { refetchBatches && refetchBatches(); } catch (e) { }
                try {
                    const now = new Date();
                    setUploadedFiles(prev => [
                        { fileName: selectedFile.name || fileName || result.fileName || 'Uploaded File', fileType: selectedFile.type || '', uploadDate: now.toISOString(), status: 'Processed', batchId: result.batchId },
                        ...prev
                    ]);
                } catch (e) { /* non-fatal */ }
                toast.success('Import successful');
            } else {
                toast.error('Import failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            toast.error('Error: ' + (err?.data?.Message || err?.message || String(err)));
        } finally {
            setIsUploading(false);
        }
    };

    // Export uploadedFiles list as CSV so user can open in Excel
    const exportUploadedFilesCsv = () => {
        // Prefer server-side list when available
        const source = (batchesResp && batchesResp.list && batchesResp.list.length) ? batchesResp.list.map(b => ({ fileName: b.FileName || b.fileName || b.BatchId || '', fileType: '', uploadDate: b.CreatedAt || b.createdAt || b.CreatedDate || null, status: b.Status || b.status || '', batchId: b.BatchId })) : uploadedFiles;
        if (!source || source.length === 0) return toast.info('No uploads to export');
        const rows = [['File Name', 'File Type', 'Upload Date', 'Status']];
        for (const f of source) {
            const d = f.uploadDate ? new Date(f.uploadDate) : (f.CreatedAt ? new Date(f.CreatedAt) : new Date());
            const fmt = d ? `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';
            rows.push([f.fileName || '', f.fileType || '', fmt, f.status || '']);
        }
        const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `uploaded_files_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };


    const onFinishImport = () => {
        if (!currentBatch) return toast.info('No batch available to finish');
        setIsFinishing(true);
        (async () => {
            // read auth token if available (some endpoints require auth)
            let authToken = null;
            try {
                const raw = localStorage.getItem('auth');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    authToken = parsed?.token || parsed?.accessToken || null;
                }
            } catch (e) { }
            if (!authToken) authToken = localStorage.getItem('token');

            try {
                const json = await finishImport({ batchId: currentBatch }).unwrap();
                if (!json) {
                    toast.error('Finish import failed: No response from server');
                    return;
                }
                toast.success(`Finish import completed. Processed ${json.processed || 0} of ${json.total || 0}. Errors: ${json.errors?.length || 0}`);
                // Refresh counts to zero or re-fetch validations
                const cjson = await validateCustomers(currentBatch).unwrap();
                if (cjson) setCounts(prev => {
                    const updated = { ...prev, invalidCustomer: cjson.counts?.InvalidCustomer || 0 };
                    const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                    updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                    return updated;
                });
                // Optionally clear batch
                // setCurrentBatch(null);
            } catch (err) {
                toast.error('Finish import failed: ' + (err?.data?.Message || err?.message || String(err)));
            } finally {
                setIsFinishing(false);
            }
        })();
    };

    const openIncorrectInsurerModal = async () => {
        if (!currentBatch) return;
        try {
            const json = await getInvalidInsurers(currentBatch).unwrap();
            setModalTitle('Incorrect Insurer Mapping');
            setModalType('insurer');
            setModalColumns({ col1: 'Incorrect Insurer in Excel', col2: 'Select Correct Insurer' });
            setModalData((json.list || []).map(r => ({ policyNo: r.PolicyNo, incorrect: r.Incorrect, correct: '', correctId: null })));
            setShowModal(true);
        } catch (err) {
            toast.error('Failed to load invalid insurers: ' + (err?.data?.Message || err?.message || String(err)));
        }
    };

    const [mappings, setMappings] = useState([
        { incorrect: "Tataa AIA", correct: "" },
        { incorrect: "HFDC Life", correct: "" },
    ]);

    const handleCardClick = (title) => {
        switch (title) {
            case 'Incorrect Insurer':
                openIncorrectInsurerModal();
                return;
            case 'Incorrect Product Name':
                if (!currentBatch) return;
                (async () => {
                    try {
                        const json = await getInvalidProducts(currentBatch).unwrap();
                        setModalTitle('Incorrect Product Mapping');
                        setModalType('product');
                        setModalColumns({ col1: 'Incorrect Product in Excel', col2: 'Select Correct Product' });
                        setModalData((json.list || []).map(r => ({ policyNo: r.PolicyNo, incorrect: r.Incorrect, correct: '' })));
                        setShowModal(true);
                    } catch (err) {
                        toast.error('Failed to load invalid products: ' + (err?.data?.Message || err?.message || String(err)));
                    }
                })();
                return;
            case 'Invalid Customer':
                if (!currentBatch) return;
                (async () => {
                    try {
                        const json = await getInvalidCustomers(currentBatch).unwrap();
                        setModalTitle('Invalid Customer Mapping');
                        setModalType('customer');
                        setModalColumns({ col1: 'Invalid Customer (Email/Mobile) in Excel', col2: 'Select Correct Customer' });
                        // Keep UploadedName, Email and Mobile in the modalData so we can use them when creating a new customer
                        setModalData((json.list || []).map((r, idx) => ({
                            policyNo: r.PolicyNo,
                            incorrect: r.UploadedName || r.Email || r.Mobile,
                            uploadedName: r.UploadedName,
                            Email: r.Email,
                            Mobile: r.Mobile,
                            matchedCustomerId: r.MatchedCustomerID || null,
                            matchedCustomerName: r.MatchedCustomerName || null,
                            matchedGroupId: r.MatchedGroupID || null,
                            // prefill correct/correctId if a match exists
                            correct: r.MatchedCustomerName || '',
                            correctId: r.MatchedCustomerID || null,
                            // auto-generated group code to show in UI when creating a new group
                            // Format: GRP + last 4 digits of phone number (if available), otherwise fallback to batch-based code
                            groupCode: (() => {
                                try {
                                    const raw = (r.Mobile || r.Mobile || '');
                                    const digits = String(raw).replace(/\D/g, '');
                                    if (digits.length >= 4) return `GRP${digits.slice(-4)}`;
                                } catch (e) { }
                                // fallback
                                return `GRP${(currentBatch || '').toString().slice(0, 4)}${String(idx + 1).padStart(2, '0')}`;
                            })(),
                            groupName: `Imported - ${r.UploadedName || r.PolicyNo || 'Group'}`,
                            // GroupID is mandatory in Customer_Master; default to creating a new group then adding customer when no match
                            customerAction: r.MatchedCustomerID ? 'map' : 'createGroupThenAdd'
                        })));
                        setShowModal(true);
                    } catch (err) {
                        toast.error('Failed to load invalid customers: ' + (err?.data?.Message || err?.message || String(err)));
                    }
                })();
                return;
            case 'Incorrect FUP Date':
                if (!currentBatch) return;
                (async () => {
                    try {
                        const json = await getInvalidFup(currentBatch).unwrap();
                        setModalTitle('Incorrect FUP Date Mapping');
                        setModalType('fup');
                        setModalColumns({ col1: 'Policy / Wrong FUP', col2: 'Select Correct FUP Date' });
                        setModalData((json.list || []).map(r => ({ policyNo: r.PolicyNo, incorrect: `${r.PolicyNo || ''} — ${formatDateShort(r.FUPDate) || ''}`, correct: '' })));
                        setShowModal(true);
                    } catch (err) {
                        toast.info('Failed to load invalid FUP dates: ' + (err?.data?.Message || err?.message || String(err)));
                    }
                })();
                return;
            case 'Incorrect Agency Details':
                if (!currentBatch) return;
                (async () => {
                    try {
                        const json = await getInvalidAgencies(currentBatch).unwrap();
                        setModalTitle('Incorrect Agency Mapping');
                        setModalType('agency');
                        setModalColumns({ col1: 'Incorrect Agency in Excel', col2: 'Select Correct Agency' });
                        setModalData((json.list || []).map(r => ({ policyNo: r.PolicyNo, incorrect: r.Incorrect, correct: '' })));
                        setShowModal(true);
                    } catch (err) {
                        toast.error('Failed to load invalid agencies: ' + (err?.data?.Message || err?.message || String(err)));
                    }
                })();
                return;
            default:
                return;
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    // helper to update per-row customerAction
    const setItemCustomerAction = (index, action) => {
        setModalData(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], customerAction: action };
            return copy;
        });
    };

    // helper to update any field on a modalData item
    const setItemField = (index, field, value) => {
        setModalData(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    };

    // Determine which uploads to show in the table.
    // Show only the current batch if available, otherwise show the most-recent uploaded file,
    // or fall back to the first server batch when present.
    const displaySource = (() => {
        try {
            if (currentBatch) {
                if (batchesResp && batchesResp.list && batchesResp.list.length) {
                    const found = batchesResp.list.find(b => String(b.BatchId || b.batchId || '') === String(currentBatch));
                    if (found) return [found];
                }
                if (uploadedFiles && uploadedFiles.length) {
                    const foundLocal = uploadedFiles.find(f => String(f.batchId || f.BatchId || '') === String(currentBatch));
                    if (foundLocal) return [foundLocal];
                }
            }
            if (uploadedFiles && uploadedFiles.length) return [uploadedFiles[0]]; // most-recent local upload
            if (batchesResp && batchesResp.list && batchesResp.list.length) return [batchesResp.list[0]]; // fallback to latest server batch
        } catch (e) { }
        return [];
    })();

    return (
        <div className="max-w-6xl mx-auto px-6 py-8">
            {/* Inline loader (do not blank entire screen) - uses shared Loader component (inline by default) */}
            {/* {(isUploading || isFinishing) && (
                <div className="w-full flex items-center justify-center py-4">
                    <Loader overlay={false} />
                </div>
            )} */}
            {/* Header */}
            <div className="flex justify-between items-center border-b border-brandRed pb-2 mb-4">
                <div>
                    <h5 className="text-brandRed font-bold">Life Insurance Import Policy Data</h5>
                </div>
                <div className="pt-1">
                    <a
                        href="/LifePolicies_Sample.xls"
                        download="LifePolicies_Sample.xls"
                        className="text-blue-700 font-medium hover:underline"
                    >
                        Download Sample File
                    </a>
                </div>

            </div>
            {/* File input row */}
            <div className="flex items-center gap-6 mb-8">
                <label className="w-40 text-gray-600 font-medium">File to be upload</label>
                <div className="flex-1 flex items-center gap-4">
                    <div className="flex-1">
                        <input
                            id="hidden-file-input"
                            type="file"
                            accept=".xls,.xlsx,.csv"
                            onChange={onFileChange}
                            className="hidden"
                        />
                        <div className="bg-white border border-gray-300 rounded shadow-sm h-11 flex items-center px-3">
                            <span className="text-sm text-gray-500 truncate">{fileName || 'No file chosen'}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onBrowseClick}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow-md"
                    >
                        <div className="w-5 h-5 flex items-center justify-center">
                            <FiUpload className="w-5 h-5" />
                        </div>
                        <span className="font-semibold">Browse</span>
                    </button>
                </div>
            </div>
            {/* Start Importing Button centered */}
            <div className="flex justify-center mb-8">
                <button
                    onClick={onStartImport}
                    disabled={isUploading || isFinishing}
                    className={`backButton ${isUploading || isFinishing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {isUploading ? (
                        <span className="inline-flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                            Processing...
                        </span>
                    ) : 'Start Importing'}
                </button>
            </div>
            {/* Table area */}
            <div className="mt-6">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="border border-gray-300 px-4 py-3 text-left text-sm text-gray-700">File Name</th>
                            <th className="border border-gray-300 px-4 py-3 text-left text-sm text-gray-700">File Type</th>
                            <th className="border border-gray-300 px-4 py-3 text-left text-sm text-gray-700">Upload Date</th>
                            <th className="border border-gray-300 px-4 py-3 text-left text-sm text-gray-700">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displaySource && displaySource.length ? (
                            displaySource.map((f, idx) => {
                                const d = f.uploadDate ? new Date(f.uploadDate) : (f.CreatedAt ? new Date(f.CreatedAt) : null);
                                const fmt = d ? `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';
                                const displayName = f.FileName || f.fileName || f.BatchId || (f.batchId || '');
                                return (
                                    <tr key={f.BatchId || f.batchId || idx}>
                                        <td className="border border-gray-300 px-4 py-2 h-10">{displayName}</td>
                                        <td className="border border-gray-300 px-4 py-2">{f.FileType || f.fileType || '-'}</td>
                                        <td className="border border-gray-300 px-4 py-2">{fmt}</td>
                                        <td className="border border-gray-300 px-4 py-2">{f.Status || f.status || '-'}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td className="border border-gray-300 px-4 py-2 h-10" colSpan={4}>No uploaded files yet</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {/* Small footer with excel icon on right */}
            <div className="flex justify-end mt-6">
                <div className="flex items-center gap-2 text-gray-700">
                    <button type="button" onClick={exportUploadedFilesCsv} className="flex items-center gap-2 text-sm text-gray-700 hover:text-black">
                        <RiFileExcel2Line size={24} />
                        <span>Export to Excel</span>
                    </button>
                </div>
            </div>
            {/* ---------- Summary metrics block (single-line titles) ---------
                Render only after a file has been processed (when totalInExcel > 0)
            */}
            {counts.totalInExcel > 0 && (
                <div className="mt-12">
                    <h6 className="text-secondary fw-bold mb-3 mt-5 bg-gray-100 rounded p-2">
                        Total No of Policies in Excel : {counts.totalInExcel}
                    </h6>
                    {/* Metrics grid: responsive 3 columns x 2 rows */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-10 gap-x-6 items-center mb-8">
                        <CardSingleLine
                            title="Incorrect Insurer"
                            count={counts.incorrectInsurer}
                            borderColor="border-red-500"
                            onClick={() => handleCardClick('Incorrect Insurer')}
                        />
                        <CardSingleLine
                            title="Incorrect Product Name"
                            count={counts.incorrectProduct}
                            borderColor="border-red-500"
                            onClick={() => handleCardClick('Incorrect Product Name')}
                        />
                        <CardSingleLine
                            title="Invalid Customer"
                            count={counts.invalidCustomer}
                            borderColor="border-red-500"
                            onClick={() => handleCardClick('Invalid Customer')}
                        />
                        <CardSingleLine
                            title="Incorrect FUP Date"
                            count={counts.incorrectFupDate}
                            borderColor="border-red-500"
                            onClick={() => handleCardClick('Incorrect FUP Date')}
                        />
                        <CardSingleLine
                            title="Incorrect Agency Details"
                            count={counts.incorrectAgency}
                            borderColor="border-red-500"
                            onClick={() => handleCardClick('Incorrect Agency Details')}
                        />
                        <CardSingleLine
                            title="No of Policies can be imported"
                            count={counts.canBeImported}
                            borderColor="border-green-500"
                        />
                    </div>
                    {/* Finish Import button centered */}
                    <div className="flex justify-center mb-4">
                        {/** Disable finish until all invalid counts are zero and there is at least one row to import */}
                        <button
                            onClick={onFinishImport}
                            disabled={isUploading || isFinishing || !(counts.incorrectInsurer === 0 && counts.incorrectProduct === 0 && counts.invalidCustomer === 0 && counts.incorrectFupDate === 0 && counts.incorrectAgency === 0 && counts.totalInExcel > 0)}
                            className={`saveButton inline-flex items-center gap-3 ${isUploading || isFinishing || !(counts.incorrectInsurer === 0 && counts.incorrectProduct === 0 && counts.invalidCustomer === 0 && counts.incorrectFupDate === 0 && counts.incorrectAgency === 0 && counts.totalInExcel > 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isFinishing ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                    <span className="font-semibold">Finishing...</span>
                                </>
                            ) : (
                                <>
                                    <FiSave className="w-5 h-5" />
                                    <span className="font-semibold">Finish Import</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Modal/Popup for Mapping */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl mx-4">
                        <div className="flex justify-between items-center border-b border-gray-200 p-4">
                            <h3 className="text-lg font-semibold text-gray-800">{modalTitle}</h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-4">
                            <table className="w-full border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left p-2 border border-gray-300 font-medium text-gray-600">
                                            {modalColumns.col1}
                                        </th>
                                        <th className="text-left p-2 border border-gray-300 font-medium text-gray-600">
                                            {modalColumns.col2}
                                        </th>
                                        <th className="text-left p-2 border border-gray-300 font-medium text-gray-600">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalData.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="p-2 border border-gray-300 font-semibold cursor-pointer">
                                                {item.incorrect}
                                            </td>
                                            <td className="p-2 border border-gray-300">
                                                {modalType === 'agency' && (
                                                    <Formik initialValues={{ Agency: item.correctId || '' }} enableReinitialize onSubmit={() => { }}>
                                                        <Form>
                                                            <AgencyDropdown name="Agency" onChange={(e) => {
                                                                const selId = e.target.value || '';
                                                                const selText = e.target.options[e.target.selectedIndex]?.text || '';
                                                                setItemField(index, 'correct', selText);
                                                                setItemField(index, 'correctId', selId);
                                                            }} />
                                                        </Form>
                                                    </Formik>
                                                )}
                                                {modalType === 'insurer' && (
                                                    <Formik initialValues={{ insurer: item.correctId || '' }} enableReinitialize onSubmit={() => { }}>
                                                        <Form>
                                                            <InsurerDropdown name="insurer" onChange={(e) => {
                                                                const selId = e.target.value || '';
                                                                const selText = e.target.options[e.target.selectedIndex]?.text || '';
                                                                setItemField(index, 'correct', selText);
                                                                setItemField(index, 'correctId', selId);
                                                            }} />
                                                        </Form>
                                                    </Formik>
                                                )}
                                                {modalType === 'product' && (
                                                    <Formik initialValues={{ productId: item.correctId || '' }} enableReinitialize onSubmit={() => { }}>
                                                        <Form>
                                                            <ProductDropdown name="productId" onChange={(e) => {
                                                                const selId = Number(e.target.value) || '';
                                                                const selText = e.target.options[e.target.selectedIndex]?.text || '';
                                                                setItemField(index, 'correct', selText);
                                                                setItemField(index, 'correctId', selId);
                                                            }} onProductSelect={(p) => {
                                                                if (p) {
                                                                    setItemField(index, 'correct', p.ProductName || p.ProductName || '');
                                                                    setItemField(index, 'correctId', p.id);
                                                                }
                                                            }} />
                                                        </Form>
                                                    </Formik>
                                                )}
                                                {modalType === 'customer' && (
                                                    <div>
                                                        <div className="mb-3">
                                                            {item.matchedCustomerId ? (
                                                                <>
                                                                    <label className="flex items-center gap-2">
                                                                        <input type="radio" name={`customerAction_${index}`} value="map" checked={item.customerAction === 'map'} onChange={() => setItemCustomerAction(index, 'map')} />
                                                                        <span className="text-sm">Map with existing customer ({item.matchedCustomerName || ''})</span>
                                                                    </label>
                                                                    <label className="flex items-center gap-2">
                                                                        <input type="radio" name={`customerAction_${index}`} value="createGroupThenAdd" checked={item.customerAction === 'createGroupThenAdd'} onChange={() => setItemCustomerAction(index, 'createGroupThenAdd')} />
                                                                        <span className="text-sm">Create new group then add customer</span>
                                                                    </label>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {/* <label className="flex items-center gap-2">
                                                                        <input type="radio" name={`customerAction_${index}`} value="create" checked={item.customerAction === 'create'} onChange={() => setItemCustomerAction(index, 'create')} />
                                                                        <span className="text-sm">Create new customer</span>
                                                                    </label> */}
                                                                    <label className="flex items-center gap-2">
                                                                        <input type="radio" name={`customerAction_${index}`} value="createGroupThenAdd" checked={item.customerAction === 'createGroupThenAdd'} onChange={() => setItemCustomerAction(index, 'createGroupThenAdd')} />
                                                                        <span className="text-sm">Create new group then add customer</span>
                                                                    </label>
                                                                </>
                                                            )}
                                                        </div>
                                                        {item.customerAction === 'map' ? (
                                                            <Formik initialValues={{ customer: '' }} onSubmit={() => { }}>
                                                                <Form>
                                                                    <CustomerDropdown name={`customer_${index}`} onCustomerSelect={(selected) => {
                                                                        // store selected customer id and display text on the item so save handler can use them
                                                                        if (selected) {
                                                                            item.correct = selected.FullName || selected.FullName || selected.Name || '';
                                                                            item.correctId = selected.CustomerID ?? selected.id ?? selected.customerId ?? selected.CustomerId;
                                                                            item.selectedCustomer = selected;
                                                                        } else {
                                                                            item.correct = '';
                                                                            item.correctId = null;
                                                                            item.selectedCustomer = null;
                                                                        }
                                                                    }} onChange={() => { }} />
                                                                </Form>
                                                            </Formik>
                                                        ) : (
                                                            <div className="space-y-2">
                                                                {/* Show values from the uploaded Excel - user should not re-enter these */}
                                                                <div className="w-full p-2 border border-gray-200 rounded bg-gray-50 text-gray-700">{item.uploadedName || item.incorrect || '(no name)'}</div>
                                                                <div className="w-full p-2 border border-gray-200 rounded bg-gray-50 text-gray-700">{item.Mobile || '(no Mobile)'}</div>
                                                                <div className="w-full p-2 border border-gray-200 rounded bg-gray-50 text-gray-700">{item.Email || '(no Email)'}</div>
                                                                {item.customerAction === 'createGroupThenAdd' && (
                                                                    <div className="mt-3 space-y-2">
                                                                        <label className="text-sm text-gray-600">Group Code (auto)</label>
                                                                        <input type="text" readOnly value={item.groupCode || ''} className="w-full p-2 border border-gray-300 rounded bg-gray-50" />
                                                                        <label className="text-sm text-gray-600">Group Name</label>
                                                                        <input type="text" value={item.groupName || ''} onChange={(e) => setItemField(index, 'groupName', e.target.value)} className="w-full p-2 border border-gray-300 rounded" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {modalType === 'fup' && (
                                                    <input type="date" className="w-full p-2 border border-gray-300 rounded shadow-sm focus:outline-none" onChange={(e) => { item.correct = e.target.value; }} />
                                                )}
                                            </td>
                                            <td className="p-2 border border-gray-300 text-center">
                                                <button className="p-2 rounded hover:bg-gray-100 transition" onClick={async () => {
                                                    if (!currentBatch) return;

                                                    if (modalType === 'customer' && (item.customerAction === 'create' || item.customerAction === 'createGroupThenAdd')) {
                                                        // Create new customer using values from the uploaded Excel (no manual entry required)
                                                        const FullName = item.uploadedName || item.incorrect || '';
                                                        const Mobile = item.Mobile || null;
                                                        const Email = item.Email || null;
                                                        if (!FullName) {
                                                            toast.success('Full name is missing in Excel for this row. Cannot create customer.');
                                                            return;
                                                        }
                                                        let groupIdToUse = null;
                                                        if (item.customerAction === 'createGroupThenAdd') {
                                                            // create a new group first via RTK Query mutation
                                                            try {
                                                                const grpPayload = { Mode: 'Add', GroupName: item.groupName || `Imported - ${item.uploadedName || item.policyNo || 'Group'}`, GroupCode: item.groupCode };
                                                                const grpResult = await saveGroup(grpPayload).unwrap();
                                                                console.debug('saveGroup result:', grpResult);
                                                                groupIdToUse = grpResult.GroupID || grpResult.GroupId || grpResult.groupId || grpResult.GroupID1 || grpResult.groupID || null;
                                                                if (!groupIdToUse) {
                                                                    // Defensive: if group creation returned no GroupID, abort and show a clear message
                                                                    toast.success('Group creation did not return an ID. Cannot create customer without a GroupID. Please try again or create the group manually.');
                                                                    return;
                                                                }
                                                            } catch (err) {
                                                                const message = (err && err.data && (err.data.Message || err.data.message)) || err?.message || 'Failed to create group';
                                                                toast.error(message);
                                                                return;
                                                            }
                                                        }

                                                        // create customer via RTK Query mutation (this will include auth headers automatically)
                                                        let createJsonLocal = undefined;
                                                        let newCustomerIdLocal = undefined;
                                                        try {
                                                            // create payload matching backend `createOrUpdateCustomer` expected keys
                                                            const customerPayload = {
                                                                Mode: 'Add',
                                                                fullName: FullName,
                                                                shortName: FullName,
                                                                initial: '',
                                                                customerType: 'Individual',
                                                                mobile: Mobile,
                                                                email: Email,
                                                                GroupID: groupIdToUse,
                                                                AreaID: null
                                                            };
                                                            createJsonLocal = await saveCustomer(customerPayload).unwrap();
                                                            console.debug('saveCustomer result:', createJsonLocal);
                                                            // createJson shape may vary — try common keys
                                                            newCustomerIdLocal = createJsonLocal.customerId || createJsonLocal.CustomerID || createJsonLocal.CustomerId || createJsonLocal.customerID || createJsonLocal.customerid || createJsonLocal?.data?.CustomerID;
                                                            if (!newCustomerIdLocal) {
                                                                toast.error(createJsonLocal.Message || createJsonLocal.message || 'Failed to create customer');
                                                                return;
                                                            }
                                                            // proceed to apply correction below
                                                        } catch (err) {
                                                            const message = (err && err.data && (err.data.Message || err.data.message)) || err?.message || 'Failed to create customer';
                                                            toast.error(message);
                                                            return;
                                                        }
                                                        // Update the staging table to attach the newly created customer (use returned customerId)
                                                        // Use the customer id returned by RTK mutation
                                                        const newCustomerId = (typeof newCustomerIdLocal !== 'undefined') ? newCustomerIdLocal : (createJsonLocal && (createJsonLocal.customerId || createJsonLocal.customerID || createJsonLocal.customerid));
                                                        const body = { batchId: currentBatch, policyNo: (item.policyNo || ''), field: 'customer' };
                                                        if (newCustomerId) body.valueId = newCustomerId;
                                                        if (groupIdToUse) body.groupId = groupIdToUse;
                                                        const json = await applyCorrection(body).unwrap();
                                                        if (json) {
                                                            setModalData(prev => prev.filter((_, i) => i !== index));
                                                            const cjson = await validateCustomers(currentBatch).unwrap();
                                                            if (cjson) setCounts(prev => {
                                                                const updated = { ...prev, invalidCustomer: cjson.counts?.InvalidCustomer || 0 };
                                                                const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                                                                updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                                                                return updated;
                                                            });
                                                        } else {
                                                            toast.error('Save failed');
                                                        }
                                                    } else {
                                                        // Regular correction for other types or mapping existing customer
                                                        const body = { batchId: currentBatch, policyNo: (item.policyNo || ''), field: modalType };
                                                        if (modalType === 'agency' || modalType === 'insurer' || modalType === 'product') {
                                                            // Prefer sending the selected ID (valueId) when available so backend can set *_Id columns.
                                                            if (typeof item.correctId !== 'undefined' && item.correctId !== null && item.correctId !== '') {
                                                                // product IDs may be numeric
                                                                const parsedId = (modalType === 'product') ? Number(item.correctId) : (isNaN(Number(item.correctId)) ? item.correctId : Number(item.correctId));
                                                                body.valueId = parsedId;
                                                                body.valueText = item.correct || null;
                                                            } else {
                                                                // fallback to text-only correction
                                                                body.valueText = item.correct;
                                                            }
                                                        }
                                                        // For customer mapping, prefer to send valueId (customer id) so backend will set CustomerId in staging
                                                        if (modalType === 'customer' && item.customerAction === 'map') {
                                                            body.valueId = item.correctId || item.selectedCustomer?.CustomerID || item.selectedCustomer?.id || item.matchedCustomerId;
                                                            // pass the customer's group id when mapping so backend can store it on staging if needed
                                                            body.groupId = item.selectedCustomer?.GroupID || item.selectedCustomer?.GroupId || item.selectedCustomer?.groupId || item.matchedGroupId || null;
                                                        } else if (modalType === 'customer') {
                                                            // If customer modal but not mapping (fallback), send name
                                                            body.valueText = item.correct;
                                                        }
                                                        if (modalType === 'fup') body.valueText = item.correct;
                                                        const json = await applyCorrection(body).unwrap();
                                                        if (json) {
                                                            // Remove corrected row from modal list
                                                            setModalData(prev => prev.filter((_, i) => i !== index));
                                                            // Refresh counts for that category
                                                            if (modalType === 'agency') {
                                                                const ajson = await validateAgencies(currentBatch).unwrap();
                                                                if (ajson) setCounts(prev => {
                                                                    const updated = { ...prev, incorrectAgency: ajson.counts?.IncorrectAgency || 0 };
                                                                    const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                                                                    updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                                                                    return updated;
                                                                });
                                                            } else if (modalType === 'insurer') {
                                                                const vjson = await validateInsurers(currentBatch).unwrap();
                                                                if (vjson) setCounts(prev => {
                                                                    const updated = { ...prev, incorrectInsurer: vjson.counts?.IncorrectInsurer || 0 };
                                                                    const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                                                                    updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                                                                    return updated;
                                                                });
                                                            } else if (modalType === 'product') {
                                                                const pjson = await validateProducts(currentBatch).unwrap();
                                                                if (pjson) setCounts(prev => {
                                                                    const updated = { ...prev, incorrectProduct: pjson.counts?.IncorrectProduct || 0 };
                                                                    const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                                                                    updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                                                                    return updated;
                                                                });
                                                            } else if (modalType === 'fup') {
                                                                const fjson = await validateFup(currentBatch).unwrap();
                                                                if (fjson) setCounts(prev => {
                                                                    const updated = { ...prev, incorrectFupDate: fjson.counts?.IncorrectFUP || 0 };
                                                                    const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                                                                    updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                                                                    return updated;
                                                                });
                                                            } else if (modalType === 'customer') {
                                                                const cjson = await validateCustomers(currentBatch).unwrap();
                                                                if (cjson) setCounts(prev => {
                                                                    const updated = { ...prev, invalidCustomer: cjson.counts?.InvalidCustomer || 0 };
                                                                    const sumInvalid = (updated.incorrectInsurer || 0) + (updated.incorrectAgency || 0) + (updated.incorrectProduct || 0) + (updated.incorrectFupDate || 0) + (updated.invalidCustomer || 0);
                                                                    updated.canBeImported = Math.max(0, (updated.totalInExcel || 0) - sumInvalid);
                                                                    return updated;
                                                                });
                                                            }
                                                        }
                                                        else { toast.error(json.error || 'Save failed'); }
                                                    }
                                                }}>
                                                    <Save size={20} className="text-gray-700" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-center p-4 border-t border-gray-200" style={{maxHeight: '80vh', overflowY: 'auto'}}>
                            <button
                                onClick={closeModal}
                                className="inline-flex items-center gap-3 bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg shadow-md"
                            >
                                <IterationCw className="w-5 h-5" />
                                <span className="font-semibold">Return to Invalid Data</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------------------------
   Presentational sub-component
   ------------------------- */
function CardSingleLine({ title, count, borderColor = 'border-red-500', onClick }) {
    return (
        <div className="flex flex-col items-center cursor-pointer" onClick={onClick}>
            <a
                href="#"
                className="text-blue-700 font-medium text-center text-sm whitespace-nowrap truncate max-w-[240px]"
                title={title}
            >
                {title}
            </a>
            <div className={`w-20 h-20 rounded-full border-4 ${borderColor} flex items-center justify-center mt-4 shadow-sm`}>
                <span className="text-2xl font-medium text-gray-700">{count}</span>
            </div>
        </div>
    );
}
